import { Link } from "react-router-dom";
import { Phone, Mail } from "lucide-react";

/**
 * One shell for every legal document on the site.
 *
 * The consent package asks us to store the exact version of the text a customer accepted,
 * so the version string is rendered on the page itself rather than buried in a comment.
 * That way the version a customer can see and the version we store are provably the same one.
 *
 * sections: [{ h: "1. Heading", p: ["para", ...], ul: ["bullet", ...] }]
 */
export default function LegalDoc({ title, subtitle, effective, version, sections, testid }) {
  return (
    <div data-testid={testid} className="py-16 sm:py-24">
      <div className="max-w-3xl mx-auto px-6 lg:px-8">
        <h1 className="font-outfit text-4xl sm:text-5xl font-bold text-paw-text tracking-tight mb-4">{title}</h1>
        {subtitle && <p className="font-manrope text-base text-paw-text-secondary mb-3">{subtitle}</p>}
        <p className="font-manrope text-sm text-paw-text-muted mb-12" data-testid="legal-version">
          Effective {effective}. Version {version}.
        </p>

        <div className="prose prose-sm max-w-none space-y-8">
          {(sections || []).map((s, i) => (
            <section key={i}>
              <h2 className="font-outfit text-xl font-semibold text-paw-text mb-3">{s.h}</h2>
              {(s.p || []).map((t, j) => (
                <p key={j} className="font-manrope text-sm text-paw-text-secondary leading-relaxed mb-3">{t}</p>
              ))}
              {(s.ul || []).length > 0 && (
                <ul className="space-y-2 font-manrope text-sm text-paw-text-secondary">
                  {s.ul.map((t, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-paw-primary mt-1.5 flex-shrink-0" />
                      {t}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-14 rounded-2xl border border-paw-border bg-paw-cream/50 px-6 py-5">
          <p className="font-manrope text-sm text-paw-text-secondary leading-relaxed mb-3">
            Questions about anything on this page? We would rather you asked than guessed.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <a href="mailto:support@pawlinx.com" className="inline-flex items-center gap-2 font-manrope text-sm font-semibold text-paw-accent hover:underline">
              <Mail className="w-4 h-4" /> support@pawlinx.com
            </a>
            <a href="tel:+18888272717" className="inline-flex items-center gap-2 font-manrope text-sm font-semibold text-paw-accent hover:underline">
              <Phone className="w-4 h-4" /> 1-888-827-2717
            </a>
          </div>
          <p className="font-manrope text-xs text-paw-text-muted mt-3">Mon to Fri 8AM to 6PM, Sat 8AM to 3PM PST.</p>
        </div>

        <p className="font-manrope text-xs text-paw-text-muted mt-8 text-center">
          See also our <Link to="/terms" className="text-paw-accent hover:underline">Terms of Service</Link>,{" "}
          <Link to="/privacy" className="text-paw-accent hover:underline">Privacy Policy</Link>,{" "}
          <Link to="/client-authorization" className="text-paw-accent hover:underline">Client Authorization Agreement</Link>,{" "}
          <Link to="/shipping-refund" className="text-paw-accent hover:underline">Shipping and Refund Policy</Link> and{" "}
          <Link to="/partner-disclosure" className="text-paw-accent hover:underline">Partner Disclosure</Link>.
        </p>
      </div>
    </div>
  );
}
